import { InputHTMLAttributes, useCallback } from 'react'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../Typography'

interface RadioProps extends InputHTMLAttributes<HTMLInputElement> {
    readonly label?: string
    readonly size?: number
    readonly inputClassName?: string
    readonly value?: string
    readonly handleChange?: (value?: string) => void
}

function Radio({
    size = 20,
    label,
    className,
    disabled,
    inputClassName,
    value,
    handleChange,
    onClick,
    ...props
}: RadioProps): JSX.Element {
    const onHandleClick = useCallback(
        (e: React.MouseEvent<HTMLInputElement>): void => {
            onClick?.(e)
            handleChange?.(value)
        },
        [handleChange, onClick, value]
    )

    return (
        <label
            className={twMerge(
                'inline-flex items-center cursor-pointer',
                className
            )}
        >
            <span
                className={twMerge(
                    'relative bg-white border border-brand rounded-full p-0.5 inline-flex',
                    disabled ? 'border-neutral-400' : ''
                )}
                style={{
                    width: size,
                    height: size,
                }}
            >
                <input
                    type="radio"
                    onClick={onHandleClick}
                    disabled={disabled}
                    className={twMerge(
                        'appearance-none relative',
                        'bg-transparent w-full h-full rounded-full scale-0',
                        'checked:bg-brand checked:scale-100',
                        'disabled:bg-neutral-400',
                        'transition-all ease-out duration-300',
                        inputClassName
                    )}
                    value={value}
                    {...props}
                />
            </span>
            {label && (
                <Typography.Caption variant="small" as="span" className="ml-2">
                    {label}
                </Typography.Caption>
            )}
        </label>
    )
}

export { Radio }
